/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @sfdc-extension-file SFDC_EXT_RATINGS_REVIEWS */
// RGBARRY-CUSTOMIZATION: simplified rating summary (no hover popover / distribution)
import { type ReactElement, useMemo, useCallback } from 'react';
import { StarRating } from '@/components/product-ratings/star-rating';
import { useProductReviews } from '@/extensions/ratings-reviews/providers/product-reviews-context';
import { cn } from '@/lib/utils';

const CUSTOMER_REVIEWS_ID = 'customer-reviews';

/**
 * Star rating summary shown under the product description on PDP.
 * Clicking scrolls to the customer reviews section and expands it.
 */
export function ProductRatingSummary({ interactive = true }: { interactive?: boolean }): ReactElement | null {
    const { reviewsSummary, reviews, expandReviews, registerOnExpanded } = useProductReviews();

    const aggregateRating = useMemo(() => {
        if (reviews.length > 0) {
            const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
            return { average: sum / reviews.length, count: reviews.length };
        }
        return {
            average: reviewsSummary?.averageRating ?? 0,
            count: reviewsSummary?.totalCount ?? 0,
        };
    }, [reviews, reviewsSummary]);

    const hasReviews = aggregateRating.count > 0 && aggregateRating.average > 0;
    const canInteract = interactive && hasReviews;

    const scrollToReviews = useCallback(() => {
        const target = document.getElementById(CUSTOMER_REVIEWS_ID);
        if (!target) return;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Move keyboard focus to the reviews section, not just the viewport. Without this a
        // keyboard or screen-reader user stays on the rating summary while the page scrolls
        // away beneath them. `preventScroll` keeps the smooth scroll above from being cut short
        // by the focus call. The target carries tabIndex={-1} so it can receive focus. (W-23325662)
        target.focus({ preventScroll: true });
    }, []);

    const handleClick = useCallback(() => {
        if (!canInteract) return;
        registerOnExpanded(scrollToReviews);
        expandReviews();
    }, [canInteract, expandReviews, registerOnExpanded, scrollToReviews]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (!canInteract) return;
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick();
            }
        },
        [canInteract, handleClick]
    );

    if (!hasReviews) return null;

    return (
        <div className="relative mt-2 inline-block">
            {/* oxlint-disable-next-line jsx-a11y/no-static-element-interactions -- role="button" and handlers are applied together, gated on the same canInteract flag */}
            <div
                className={cn('relative inline-block', canInteract && 'cursor-pointer')}
                onClick={canInteract ? handleClick : undefined}
                onKeyDown={canInteract ? handleKeyDown : undefined}
                role={canInteract ? 'button' : undefined}
                tabIndex={canInteract ? 0 : undefined}
                aria-label={canInteract ? 'View customer reviews' : undefined}>
                <div className="flex items-center gap-2">
                    <StarRating
                        rating={aggregateRating.average}
                        reviewCount={aggregateRating.count}
                        showRatingLabel={false}
                        showRatingLink={false}
                        starSize="default"
                        starClassName="text-[#1a1a1a]"
                    />
                    {aggregateRating.count > 0 && (
                        <span className="text-sm text-muted-foreground">({aggregateRating.count})</span>
                    )}
                </div>
            </div>
        </div>
    );
}
